import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Autocomplete } from '@mui/material';

import useComponentMounted from '../../../hooks/useComponentMounted';
import useToast from '../../hooks/useToast';
import User from '../../../Model/User';
import DialogRoundedModal from '../../../components/DialogRoundedModal';
import { SaveButton, CancelButton } from '../../../components/Button/ActionButtons';
import texts from './texts.json';
import {
  ButtonContainer,
  InputContainer,
  StyledTextLabel,
  AutocompleteTextField,
} from './styles';

const LeadAssignmentModal = ({
  open,
  onClose,
  selectedUser,
  mode,
  refreshUserList,
}) => {
  const [selectedLead, setSelectedLead] = useState({ id: null, name: null });
  const [isLoading, setIsLoading] = useState(false);
  const [liaisonUsers, setLiaisonUsers] = useState([]);

  const { showToast } = useToast();
  const isComponentMountedRef = useComponentMounted();

  useEffect(() => {
    const fetchLeads = async () => {
      if (open) {
        setLiaisonUsers([]);
        try {
          setIsLoading(true);
          const users = await User.getInternalUsers();
          if (isComponentMountedRef.current) {
            const filteredUsers = users
              .filter((user) => user.isLiaison && user.id !== selectedUser?.id);
            setLiaisonUsers(filteredUsers);
            setIsLoading(false);
          }
        } catch (error) {
          showToast(texts.errorFetchingLeads, { error: true });
        } finally {
          if (isComponentMountedRef.current) {
            setIsLoading(false);
          }
        }
      }
    };
    fetchLeads();
  }, [open, isComponentMountedRef, showToast, selectedUser]);

  const handleClose = () => {
    setSelectedLead({ id: null, name: null });
    onClose();
  };

  const handleSave = async () => {
    if (!selectedLead?.id) return;
    setIsLoading(true);
    try {
      const userCol = await User.getUserByEmail(selectedUser.email);
      if (!userCol.hasDocs) {
        showToast(texts.userNotFound, { error: true });
        return;
      }
      const userDoc = userCol.docs[0];

      await userDoc.updateFields({
        leadId: selectedLead.id,
        leadName: selectedLead.name,
      });
      showToast(mode === 'assign' ? texts.leadAssignedSuccessfully : texts.leadUpdatedSuccessfully);
      refreshUserList();
      handleClose();
    } catch (error) {
      showToast(texts.errorAssigningLead, { error: true });
    } finally {
      if (isComponentMountedRef.current) {
        setIsLoading(false);
        onClose();
      }
    }
  };

  return (
    <DialogRoundedModal
      open={open}
      onClose={handleClose}
      title={mode === 'assign' ? texts.assignLead : texts.updateLead}
      fullWidth
      maxWidth="sm"
      actionButtons={(
        <ButtonContainer>
          <SaveButton
            variant="contained"
            onClick={handleSave}
            disabled={!selectedLead.id || isLoading}
          >
            {texts.save}
          </SaveButton>
          <CancelButton onClick={handleClose}>{texts.cancel}</CancelButton>
        </ButtonContainer>
      )}
    >
      <InputContainer>
        <StyledTextLabel>{texts.leadInputLabel}</StyledTextLabel>
        <Autocomplete
          fullWidth
          options={liaisonUsers}
          loading={isLoading}
          loadingText={texts.loadingLeads}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(_, selection) => setSelectedLead({
            id: selection?.id,
            name: selection?.name,
          })}
          getOptionLabel={(option) => `${option.name} (${option.email})`}
          renderInput={(params) => (
            <AutocompleteTextField
              {...params}
              fullWidth
              placeholder={texts.selectLead}
            />
          )}
        />
      </InputContainer>
    </DialogRoundedModal>
  );
};

LeadAssignmentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedUser: PropTypes.object.isRequired,
  mode: PropTypes.oneOf(['assign', 'update']).isRequired,
  refreshUserList: PropTypes.func.isRequired,
};

export default LeadAssignmentModal;
